import React from 'react';
import { ActionMenu } from '@/components/ui/ActionMenu';
import { CardList } from '@/components/ui/CardList';
import { cn } from '@/utils/cn';
import { usePermission } from '@/core/permissions/usePermission';
import type { TeamListItem } from '../types/teamsList.types';

interface TeamsListTableProps {
  items: TeamListItem[];
  isLoading: boolean;
  onView: (team: TeamListItem) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  selectedId?: string;
}

export function TeamsListTable({ items, isLoading, onView, onEdit, onDelete, selectedId }: TeamsListTableProps) {
  const { can } = usePermission();
  const canUpdate = can('teams' as any, 'update' as any);
  const canDelete = can('teams' as any, 'delete' as any);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active': return { label: 'Đang hoạt động', class: 'bg-emerald-50 text-emerald-600 border-emerald-100' };
      case 'inactive': return { label: 'Tạm ngưng', class: 'bg-amber-50 text-amber-600 border-amber-100' };
      case 'on_leave': return { label: 'Đã giải thể', class: 'bg-rose-50 text-rose-600 border-rose-100' };
      default: return { label: 'N/A', class: 'bg-slate-50 text-slate-400 border-slate-100' };
    }
  };

  const getActionItems = (team: TeamListItem) => {
    const actions = [
      { label: 'Xem chi tiết', onClick: () => onView(team) }
    ];
    if (canUpdate) actions.push({ label: 'Chỉnh sửa', onClick: () => onEdit(team.id) });
    if (canDelete) actions.push({ label: 'Giải thể team', destructive: true, onClick: () => onDelete(team.id) });
    return actions;
  };

  const renderMobileCard = (team: TeamListItem) => {
    const status = getStatusBadge(team.status_code);
    return (
      <div 
        key={team.id}
        onClick={() => onView(team)}
        className={cn(
          "bg-white rounded-md p-3 border border-slate-100 space-y-3 transition-all active:scale-95",
          selectedId === team.id && "ring-1 ring-emerald-500 bg-emerald-50/20"
        )}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center border border-blue-100 font-normal">
              {team.name[0]}
            </div>
            <div>
              <div className="sm:text-sm text-xs text-slate-900 font-normal">{team.name}</div>
              <div className="sm:text-sm text-xs text-slate-400 font-normal tracking-wider">{team.code}</div>
            </div>
          </div>
          <span className={cn("px-2 py-0.5 rounded-md border sm:text-sm text-xs font-normal", status.class)}>
            {status.label}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-3 pt-1">
          <div>
            <p className="sm:text-sm text-xs text-slate-400 font-normal tracking-widest">Phòng ban</p>
            <p className="sm:text-sm text-xs text-slate-700">{team.department_name || '---'}</p>
          </div>
          <div>
            <p className="sm:text-sm text-xs text-slate-400 font-normal tracking-widest">Số thành viên</p>
            <p className="sm:text-sm text-xs text-slate-700">{team.member_count} người</p>
          </div>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-slate-50">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-slate-100 border border-white overflow-hidden flex items-center justify-center sm:text-sm text-xs font-normal">
              {team.leader?.avatar_url ? (
                <img src={team.leader.avatar_url} className="w-full h-full object-cover" />
              ) : (
                team.leader?.full_name[0] || 'L'
              )}
            </div>
            <span className="sm:text-sm text-xs text-slate-600 truncate max-w-[120px]">{team.leader?.full_name || 'Chưa phân công'}</span>
          </div>
          <ActionMenu items={getActionItems(team)} />
        </div>
      </div>
    );
  };

  return (
    <div className="flex-1 min-h-0">
      {/* Desktop View */}
      <div className="hidden lg:block bg-white rounded-md border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/50 border-b border-slate-100">
                <th className="p-3 sm:text-sm text-xs text-slate-500 tracking-wider w-12 text-center font-normal">#</th>
                <th className="p-3 sm:text-sm text-xs text-slate-500 tracking-wider font-normal">Tên team</th>
                <th className="p-3 sm:text-sm text-xs text-slate-500 tracking-wider font-normal">Mã team</th>
                <th className="p-3 sm:text-sm text-xs text-slate-500 tracking-wider font-normal">Phòng ban</th>
                <th className="p-3 sm:text-sm text-xs text-slate-500 tracking-wider font-normal">Trưởng team</th>
                <th className="p-3 sm:text-sm text-xs text-slate-500 tracking-wider font-normal text-center">Số thành viên</th>
                <th className="p-3 sm:text-sm text-xs text-slate-500 tracking-wider font-normal text-center">Trạng thái</th>
                <th className="p-3 sm:text-sm text-xs text-slate-500 tracking-wider font-normal">Mô tả</th>
                <th className="p-3 sm:text-sm text-xs text-slate-500 tracking-wider text-right font-normal">Thao tác</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 sm:text-sm text-xs">
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td colSpan={9} className="p-3"><div className="h-10 bg-slate-50 rounded-md" /></td>
                  </tr>
                ))
              ) : items.map((team, i) => {
                const status = getStatusBadge(team.status_code);
                return (
                  <tr 
                    key={team.id}
                    onClick={() => onView(team)}
                    className={cn(
                      "hover:bg-slate-50/50 transition-colors cursor-pointer group",
                      selectedId === team.id && "bg-emerald-50/30"
                    )}
                  >
                    <td className="p-3 text-slate-400 text-center font-normal">
                      {(i + 1).toString().padStart(2, '0')}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center border border-blue-100">
                          {team.name[0]}
                        </div>
                        <span className="text-slate-900 group-hover:text-emerald-600 transition-colors font-normal">{team.name}</span>
                      </div>
                    </td>
                    <td className="p-3 text-slate-500">{team.code}</td>
                    <td className="p-3 text-slate-500">{team.department_name || '---'}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-slate-100 border border-white overflow-hidden flex items-center justify-center sm:text-sm text-xs font-normal">
                          {team.leader?.avatar_url ? (
                            <img src={team.leader.avatar_url} className="w-full h-full object-cover" />
                          ) : (
                            team.leader?.full_name[0] || 'L'
                          )}
                        </div>
                        <span className="text-slate-700">{team.leader?.full_name || 'Chưa phân công'}</span>
                      </div>
                    </td>
                    <td className="p-3 text-center text-slate-600">{team.member_count}</td>
                    <td className="p-3 text-center">
                      <span className={cn("px-2 py-0.5 rounded-md border sm:text-sm text-xs font-normal", status.class)}>
                        {status.label}
                      </span>
                    </td>
                    <td className="p-3 text-slate-400 max-w-[200px] truncate">{team.description || '---'}</td>
                    <td className="p-3 text-right" onClick={e => e.stopPropagation()}>
                      <ActionMenu items={getActionItems(team)} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile View */}
      <div className="lg:hidden">
        <CardList
          items={items}
          renderCard={renderMobileCard}
          isLoading={isLoading}
          emptyText="Không tìm thấy đội nhóm nào."
        />
      </div>
    </div>
  );
}
